import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import type { SessionUser } from '../api/types.js';

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  /** 화이트리스트에 없는 IP - 로그인 자체를 막아야 한다 (middleware/ipWhitelist.ts). */
  ipBlocked: boolean;
  login: (userid: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<{ user: SessionUser | null; ipBlocked?: boolean }> => {
      try {
        return await authApi.me();
      } catch (err) {
        // 401 은 "로그인 안 됨"이라는 정상 상태다. 에러로 올리지 않는다.
        if (err instanceof ApiError && err.status === 401) return { user: null };
        // 화이트리스트에 없는 IP(middleware/ipWhitelist.ts 가 /api/auth/me 도
        // 막는다) - 로그인 폼을 보여줘 봤자 눌러도 똑같이 막히니, 시도하기 전에
        // 바로 알려준다(App.tsx 가 ipBlocked 를 보고 로그인 폼 대신 안내를 그린다).
        if (err instanceof ApiError && err.code === 'IP_NOT_ALLOWED') return { user: null, ipBlocked: true };
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const login = useCallback(
    async (userid: string, password: string) => {
      const result = await authApi.login(userid, password);
      queryClient.setQueryData(['auth', 'me'], result);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    queryClient.setQueryData(['auth', 'me'], { user: null });
    // 이전 사용자의 데이터가 남지 않도록 캐시를 비운다.
    queryClient.clear();
  }, [queryClient]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const result = await authApi.changePassword(currentPassword, newPassword);
      // 서버가 mustChangePassword=false 로 세션을 다시 발급한다 - 여기서
      // 캐시를 갱신해야 App.tsx 의 강제 변경 게이트가 바로 풀린다.
      queryClient.setQueryData(['auth', 'me'], result);
    },
    [queryClient],
  );

  return (
    <AuthContext.Provider
      value={{ user: data?.user ?? null, isLoading, ipBlocked: data?.ipBlocked ?? false, login, logout, changePassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 는 AuthProvider 안에서만 쓸 수 있습니다.');
  return ctx;
}
